import { supabase } from '../../shared/supabase/supabaseClient';

export type KitchenPrintJobStatus = 'printed' | 'retrying' | 'failed' | 'cancelled';

export type EnqueueKitchenPrintJobInput = {
  branchId: string;
  ticketId: string;
  stationId?: string | null;
  printerName?: string | null;
  dedupeKey: string;
  payload: Record<string, unknown>;
};

export async function enqueueKitchenPrintJob(input: EnqueueKitchenPrintJobInput): Promise<string> {
  const { data, error } = await supabase.rpc('enqueue_kitchen_print_job', {
    p_branch_id: input.branchId,
    p_ticket_id: input.ticketId,
    p_station_id: input.stationId ?? null,
    p_printer_name: input.printerName ?? null,
    p_dedupe_key: input.dedupeKey,
    p_payload: input.payload,
  });
  if (error) throw new Error(error.message || 'No se pudo encolar la comanda.');
  const id = typeof data?.id === 'string' ? data.id : null;
  if (!id) throw new Error('El servidor no devolvió el job de impresión.');
  return id;
}

export type KitchenPrintJob = {
  id: string;
  ticketId: string;
  stationId: string | null;
  stationName: string | null;
  printerName: string | null;
  status: 'queued' | 'retrying' | 'failed' | 'cancelled';
  attempts: number;
  lastError: string | null;
  createdAt: string | null;
  payload: { text?: string; html?: string };
};

export async function listKitchenPrintJobs(branchId: string): Promise<KitchenPrintJob[]> {
  const { data, error } = await supabase.rpc('get_kitchen_print_jobs', {
    p_branch_id: branchId,
    p_limit: 50,
  });
  if (error) throw new Error(error.message || 'No se pudieron cargar las impresiones.');
  const jobs = (data as { jobs?: unknown[] } | null)?.jobs;
  if (!Array.isArray(jobs)) return [];
  return jobs.map((row) => {
    const r = row as Record<string, unknown>;
    const payload = (r.payload ?? {}) as { text?: string; html?: string };
    const status: KitchenPrintJob['status'] =
      r.status === 'queued' || r.status === 'retrying' || r.status === 'cancelled'
        ? r.status
        : 'failed';
    return {
      id: String(r.id ?? ''),
      ticketId: String(r.ticket_id ?? ''),
      stationId: typeof r.station_id === 'string' ? r.station_id : null,
      stationName: typeof r.station_name === 'string' ? r.station_name : null,
      printerName: typeof r.printer_name === 'string' ? r.printer_name : null,
      status,
      attempts: Number(r.attempts ?? 0),
      lastError: typeof r.last_error === 'string' ? r.last_error : null,
      createdAt: typeof r.created_at === 'string' ? r.created_at : null,
      payload,
    };
  }).filter((job) => job.id);
}

export async function completeKitchenPrintJob(input: {
  jobId: string;
  status: KitchenPrintJobStatus;
  error?: string | null;
  nextAttemptAt?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('complete_kitchen_print_job', {
    p_job_id: input.jobId,
    p_status: input.status,
    p_error: input.error ?? null,
    p_next_attempt_at: input.nextAttemptAt ?? null,
  });
  if (error) throw new Error(error.message || 'No se pudo actualizar el job de impresión.');
}
